import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Building2, Users, UploadCloud, ClipboardCheck, Plus, Trash2, Check, FileText, Loader2, Lock, Landmark, Zap, Globe } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { useTranslation } from "react-i18next";
import Disclaimer from "@/components/Disclaimer";
import { ENTITY_TYPES, formatUSD, REQUIRES_CONFIRMATION } from "@/lib/constants";
import { useIdentity } from "@/context/SiteConfigContext";

const emptyDirector = { full_name: "", email: "", nationality: "", ownership: "" };
const newDirector = () => ({ ...emptyDirector, _k: crypto.randomUUID() });
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");

const INC_STEPS = [
  { key: "order", labelKey: "wizard.stepOrder", icon: ClipboardCheck },
  { key: "company", labelKey: "wizard.stepCompany", icon: Building2 },
  { key: "directors", labelKey: "wizard.stepDirectors", icon: Users },
  { key: "documents", labelKey: "wizard.stepDocuments", icon: UploadCloud },
  { key: "review", labelKey: "wizard.stepReview", icon: Check },
];
const BANK_STEPS = [
  { key: "order", labelKey: "wizard.stepOrder", icon: ClipboardCheck },
  { key: "applicant", labelKey: "wizard.stepApplicant", icon: Building2 },
  { key: "documents", labelKey: "wizard.stepDocuments", icon: UploadCloud },
  { key: "review", labelKey: "wizard.stepReview", icon: Check },
];

export default function StartRequest() {
  const navigate = useNavigate();
  const identity = useIdentity();
  const [params] = useSearchParams();
  const pkg = params.get("pkg") || "";
  const bankParam = params.get("bank") || "";
  const bankOnly = pkg.startsWith("bank_");
  const bankSlug = bankOnly ? pkg.slice(5) : null;

  const [cat, setCat] = useState({ countries: [], banks: [], addons: [] });
  const [banks, setBanks] = useState([]);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);

  const [form, setForm] = useState({
    country: params.get("country") || "", bank: "", addBank: false, addons: [], incBundle: false,
    company_name: "", entity_type: "", business_activity: "",
    contact_name: "", contact_email: "", contact_phone: "", directors: [newDirector()], notes: "",
  });

  const steps = bankOnly ? BANK_STEPS : INC_STEPS;
  const lastStep = steps.length - 1;

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setCat(r.data);
      if (pkg && pkg.startsWith("inc_")) {
        const c = (r.data.countries || []).find((x) => slug(x.name) === pkg.slice(4));
        if (c) setForm((f) => ({ ...f, country: c.name }));
      }
      if (bankParam && !bankOnly) {
        const b = (r.data.banks || []).find((x) => x.name === bankParam);
        if (b) setForm((f) => ({ ...f, addBank: true, bank: b.name }));
      }
    }).catch(() => {});
    if (bankOnly) api.get("/banks").then((r) => setBanks(r.data.banks || [])).catch(() => {});
  }, [pkg, bankOnly, bankParam]);

  const startedRef = useRef(false);
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent("form_start");
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDirector = (i, k, v) => setForm((f) => { const d = [...f.directors]; d[i] = { ...d[i], [k]: v }; return { ...f, directors: d }; });
  const toggleAddon = (name) => setForm((f) => ({ ...f, addons: f.addons.includes(name) ? f.addons.filter((a) => a !== name) : [...f.addons, name] }));

  const country = useMemo(() => cat.countries.find((c) => c.name === form.country), [cat, form.country]);
  const bank = useMemo(() => cat.banks.find((b) => b.name === form.bank), [cat, form.bank]);
  const selectedBank = useMemo(() => banks.find((b) => b.slug === bankSlug), [banks, bankSlug]);
  const includesBanking = bankOnly || (form.addBank && !!form.bank);
  const needsQuote = !!country && country.confirmation_status === "requires_confirmation";
  const bankOptions = useMemo(() => {
    const seen = new Set(); const out = [];
    (cat.banks || []).forEach((b) => { if (!seen.has(b.name)) { seen.add(b.name); out.push(b); } });
    return out;
  }, [cat]);

  const lineItems = useMemo(() => {
    const items = [];
    if (bankOnly) {
      const incC = form.incBundle ? country : null;
      if (incC) items.push({ label: t("wizard.lineCompanyFormation", { name: incC.name }), amount: incC.setup_fee });
      if (selectedBank) items.push({ label: t("wizard.lineCorporateBankAccount", { name: selectedBank.name }), amount: selectedBank.fee });
      form.addons.forEach((a) => { const x = cat.addons.find((z) => z.name === a); if (x) items.push({ label: t(`wizard.addon.${slug(a)}`, { defaultValue: a }), amount: x.price }); });
      if (incC && selectedBank) items.push({ label: t("wizard.lineBundleSaving"), amount: -Math.round((selectedBank.fee + incC.setup_fee) * 0.1) });
      return items;
    }
    if (country) items.push({ label: t("wizard.lineCompanyFormation", { name: country.name }), amount: country.setup_fee });
    if (form.addBank && bank) items.push({ label: t("wizard.lineBankAccount", { name: bank.name }), amount: bank.fee });
    form.addons.forEach((a) => { const x = cat.addons.find((z) => z.name === a); if (x) items.push({ label: a, amount: x.price }); });
    if (form.addBank && bank && country) items.push({ label: t("wizard.lineBundleSaving"), amount: -Math.round((bank.fee + country.setup_fee) * 0.1) });
    return items;
  }, [bankOnly, selectedBank, country, bank, form.addBank, form.incBundle, form.addons, cat, t]);
  const total = lineItems.reduce((s, i) => s + i.amount, 0);

  const go = (n) => { setDirection(n > step ? 1 : -1); setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const validate = () => {
    const key = steps[step].key;
    if (key === "order" && !bankOnly && !form.country) { toast.error(t("forms.selectCountryError")); return false; }
    if (key === "company" && (!form.company_name.trim() || !form.entity_type)) { toast.error(t("wizard.errCompanyEntity")); return false; }
    if (key === "applicant" && !form.company_name.trim()) { toast.error(t("wizard.errAccountHolder")); return false; }
    if (key === "directors" && !form.directors[0]?.full_name.trim()) { toast.error(t("wizard.errDirector")); return false; }
    return true;
  };
  const next = () => { if (validate()) go(Math.min(step + 1, lastStep)); };
  const back = () => go(Math.max(step - 1, 0));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocuments((d) => [...d, { id: res.data.id, filename: res.data.filename }]);
      }
      toast.success(t("wizard.uploaded"));
    } catch { toast.error(t("wizard.uploadFailed")); } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.contact_name.trim() || !form.contact_email.trim()) { toast.error(t("wizard.errContact")); return; }
    setSubmitting(true);
    try {
      const payload = bankOnly ? {
        order_type: "bank", bank_slug: bankSlug, country: form.incBundle ? form.country : null, addons: form.addons,
        company_name: form.company_name, entity_type: form.entity_type,
        jurisdiction: form.incBundle ? form.country : (selectedBank?.countries?.[0]?.name || ""), business_activity: form.business_activity,
        contact_name: form.contact_name, contact_email: form.contact_email, contact_phone: form.contact_phone,
        directors: [], document_ids: documents.map((d) => d.id), preferred_language: i18n.language,
      } : {
        order_type: "incorporation", country: form.country, bank: form.addBank ? form.bank : null, addons: form.addons,
        company_name: form.company_name, entity_type: form.entity_type, jurisdiction: form.country,
        business_activity: form.business_activity, contact_name: form.contact_name,
        contact_email: form.contact_email, contact_phone: form.contact_phone,
        directors: form.directors.map(({ _k, ...d }) => d), document_ids: documents.map((d) => d.id), preferred_language: i18n.language,
      };
      const res = await api.post("/requests", payload);
      const co = await api.post("/payments/checkout", { request_id: res.data.id, origin_url: window.location.origin });
      const pkgName = bankOnly ? `${selectedBank?.name || "Bank"} Corporate Account` : `${form.country} Company Formation`;
      trackEvent("begin_checkout", { package_name: pkgName, amount: Math.round(total * 100) / 100, currency: "usd" });
      try { localStorage.setItem("uni_pending_purchase", JSON.stringify({ session_id: co.data.session_id, amount: Math.round(total * 100) / 100, currency: "usd" })); } catch { /* noop */ }
      window.location.href = co.data.url;
    } catch { toast.error(t("wizard.errCheckout")); setSubmitting(false); }
  };

  const variants = { enter: (d) => ({ opacity: 0, x: d > 0 ? 40 : -40 }), center: { opacity: 1, x: 0 }, exit: (d) => ({ opacity: 0, x: d > 0 ? -40 : 40 }) };
  const inputCls = "mt-1.5 bg-background border-border";
  const key = steps[step].key;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-24 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between" data-testid="wizard-progress">
            {steps.map((s, i) => {
              const done = i < step, current = i === step;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center gap-1.5">
                    <span className={`grid place-items-center h-9 w-9 rounded-sm border ${done ? "bg-orange-600 border-orange-600 text-white" : current ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border text-muted-foreground"}`}>
                      {done ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider ${current ? "text-foreground" : "text-muted-foreground"}`}>{t(s.labelKey)}</span>
                  </div>
                  {i < lastStep && <span className={`flex-1 h-px mx-2 ${i < step ? "bg-orange-500" : "bg-border"}`} />}
                </React.Fragment>
              );
            })}
          </div>

          <div className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8 overflow-hidden card-soft">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
                {key === "order" && (
                  <div>
                    <h2 className="font-heading text-2xl tracking-tight text-primary">{bankOnly ? t("wizard.orderTitleBank") : t("wizard.orderTitleInc")}</h2>
                    <p className="mt-2 text-sm text-muted-foreground">{bankOnly ? t("wizard.orderSubtitleBank") : t("wizard.orderSubtitleInc")}</p>
                    <div className="mt-6 space-y-5">
                      {bankOnly ? (
                        <>
                        <div className="rounded-md border border-border p-5 bg-secondary/30" data-testid="wizard-bank-selected">
                          {selectedBank ? (
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <span className="grid place-items-center h-11 w-11 rounded-lg bg-card text-primary shrink-0"><Landmark className="h-5 w-5" /></span>
                                <div>
                                  <p className="font-heading text-lg text-primary">{selectedBank.name}</p>
                                  <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${selectedBank.type === "Digital" ? "text-blue-700 bg-blue-100" : "text-slate-700 bg-slate-100"}`}>{selectedBank.type === "Digital" ? <Zap className="h-3 w-3" /> : <Landmark className="h-3 w-3" />} {selectedBank.type}</span>
                                  <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Globe className="h-3.5 w-3.5 text-orange-600" /> {selectedBank.country_count} {selectedBank.country_count === 1 ? t("wizard.jurisdiction") : t("wizard.jurisdictions")}</p>
                                </div>
                              </div>
                              <span className="font-mono text-sm text-foreground shrink-0">{formatUSD(selectedBank.fee)}</span>
                            </div>
                          ) : <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("wizard.loadingBank")}</p>}
                        </div>
                        <div className="rounded-md border border-border p-4" data-testid="wizard-inc-bundle">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <Checkbox checked={form.incBundle} onCheckedChange={(v) => set("incBundle", !!v)} data-testid="wizard-incbundle" />
                            <span className="text-sm text-foreground flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> {t("wizard.alsoIncorporate")} <span className="text-[11px] font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5">{t("wizard.save10")}</span></span>
                          </label>
                          {form.incBundle && (
                            <>
                              <Select value={form.country} onValueChange={(v) => { set("country", v); trackEvent("select_package", { package_name: `${v} Company Formation`, jurisdiction: v }); }}>
                                <SelectTrigger className="mt-3 bg-background border-border" data-testid="wizard-bundle-country"><SelectValue placeholder={t("wizard.selectCountryIncorporate")} /></SelectTrigger>
                                <SelectContent className="max-h-72">{cat.countries.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} — {formatUSD(c.setup_fee)}</SelectItem>)}</SelectContent>
                              </Select>
                              {country && <p className="mt-2 text-xs text-green-700">{t("wizard.bundleSaving", { amount: formatUSD(Math.round(((selectedBank?.fee || 0) + country.setup_fee) * 0.1)) })}</p>}
                            </>
                          )}
                        </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <Label>{t("wizard.countryOfIncorporation")}</Label>
                            <Select value={form.country} onValueChange={(v) => { set("country", v); trackEvent("select_package", { package_name: `${v} Company Formation`, jurisdiction: v }); }}>
                              <SelectTrigger className={inputCls} data-testid="wizard-country"><SelectValue placeholder={t("wizard.selectCountry")} /></SelectTrigger>
                              <SelectContent className="max-h-72">{cat.countries.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} — {formatUSD(c.setup_fee)}</SelectItem>)}</SelectContent>
                            </Select>
                            {country && <p className="mt-1.5 text-xs text-muted-foreground">{t("wizard.setupThen", { setup: formatUSD(country.setup_fee), annual: formatUSD(country.annual_fee) })}</p>}
                          </div>
                          <div className="rounded-md border border-border p-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <Checkbox checked={form.addBank} onCheckedChange={(v) => set("addBank", !!v)} data-testid="wizard-addbank" />
                              <span className="text-sm text-foreground flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> {t("wizard.addBankAccount")}</span>
                            </label>
                            {form.addBank && (
                              <Select value={form.bank} onValueChange={(v) => set("bank", v)}>
                                <SelectTrigger className="mt-3 bg-background border-border" data-testid="wizard-bank"><SelectValue placeholder={t("wizard.selectBank")} /></SelectTrigger>
                                <SelectContent className="max-h-72">{bankOptions.map((b) => <SelectItem key={b.name} value={b.name}>{b.name} · {b.type} — {formatUSD(b.fee)}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                          </div>
                        </>
                      )}
                      <div>
                        <Label className="mb-2 block">{t("wizard.optionalAddons")}</Label>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {cat.addons.map((a) => (
                            <label key={a.name} className={`flex items-center justify-between gap-2 rounded-md border p-3 cursor-pointer transition-colors ${form.addons.includes(a.name) ? "border-orange-400 bg-orange-50/50" : "border-border hover:border-orange-300"}`} data-testid={`wizard-addon-${slug(a.name)}`}>
                              <span className="flex items-center gap-2 text-sm min-w-0"><Checkbox className="shrink-0" checked={form.addons.includes(a.name)} onCheckedChange={() => toggleAddon(a.name)} /> <span className="min-w-0">{t(`wizard.addon.${slug(a.name)}`, { defaultValue: a.name })}</span></span>
                              <span className="text-xs font-mono text-muted-foreground shrink-0">{formatUSD(a.price)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(key === "company" || key === "applicant") && (
                  <div>
                    <h2 className="font-heading text-2xl tracking-tight text-primary">{bankOnly ? t("wizard.companyTitleBank") : t("wizard.companyTitleInc")}</h2>
                    {bankOnly && <p className="mt-2 text-sm text-muted-foreground">{t("wizard.companySubtitleBank")}</p>}
                    <div className="mt-6 space-y-5">
                      <div><Label htmlFor="cn">{bankOnly ? t("wizard.labelCompanyNameBank") : t("wizard.labelCompanyNameInc")}</Label><Input id="cn" className={inputCls} placeholder={t("wizard.companyNamePlaceholder")} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="wizard-company-name" /></div>
                      <div><Label>{t("wizard.labelEntityType")} {bankOnly && <span className="text-muted-foreground">{t("wizard.entityTypeOptional")}</span>}</Label>
                        <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                          <SelectTrigger className={inputCls} data-testid="wizard-entity-type"><SelectValue placeholder={t("wizard.selectType")} /></SelectTrigger>
                          <SelectContent>{ENTITY_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label htmlFor="ba">{t("wizard.labelBusinessActivity")}</Label><Textarea id="ba" className={`${inputCls} min-h-[96px]`} placeholder={t("wizard.businessActivityPlaceholder")} value={form.business_activity} onChange={(e) => set("business_activity", e.target.value)} data-testid="wizard-activity" /></div>
                    </div>
                  </div>
                )}

                {key === "directors" && (
                  <div>
                    <h2 className="font-heading text-2xl tracking-tight text-primary">{t("wizard.directorsTitle")}</h2>
                    <div className="mt-6 space-y-4">
                      {form.directors.map((d, i) => (
                        <div key={d._k} className="rounded-md border border-border p-4 bg-secondary/30" data-testid={`wizard-director-${i}`}>
                          <div className="flex items-center justify-between mb-3"><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("wizard.person", { n: i + 1 })}</span>
                            {form.directors.length > 1 && <button onClick={() => setForm((f) => ({ ...f, directors: f.directors.filter((_, x) => x !== i) }))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                          </div>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <Input className="bg-card border-border" placeholder={t("forms.namePlaceholder")} value={d.full_name} onChange={(e) => setDirector(i, "full_name", e.target.value)} data-testid={`wizard-director-name-${i}`} />
                            <Input className="bg-card border-border" placeholder={t("wizard.emailOptional")} value={d.email} onChange={(e) => setDirector(i, "email", e.target.value)} />
                            <Input className="bg-card border-border" placeholder={t("wizard.nationality")} value={d.nationality} onChange={(e) => setDirector(i, "nationality", e.target.value)} />
                            <Input className="bg-card border-border" placeholder={t("wizard.ownership")} value={d.ownership} onChange={(e) => setDirector(i, "ownership", e.target.value)} />
                          </div>
                        </div>
                      ))}
                      <Button variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={() => setForm((f) => ({ ...f, directors: [...f.directors, newDirector()] }))} data-testid="wizard-add-director"><Plus className="h-4 w-4 mr-1.5" /> {t("wizard.addPerson")}</Button>
                    </div>
                  </div>
                )}

                {key === "documents" && (
                  <div>
                    <h2 className="font-heading text-2xl tracking-tight text-primary">{t("wizard.documentsTitle")}</h2>
                    <p className="mt-2 text-sm text-muted-foreground">{t("wizard.documentsSubtitle")}</p>
                    <label className="mt-6 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 bg-secondary/30 py-12 cursor-pointer hover:border-orange-400 transition-colors" data-testid="wizard-upload-dropzone">
                      <input type="file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleUpload} data-testid="wizard-upload-input" />
                      {uploading ? <Loader2 className="h-6 w-6 text-orange-600 animate-spin" /> : <UploadCloud className="h-6 w-6 text-muted-foreground" />}
                      <span className="text-sm text-muted-foreground">{uploading ? t("wizard.uploading") : t("wizard.clickToUpload")}</span>
                    </label>
                    {documents.length > 0 && (
                      <ul className="mt-5 space-y-2">{documents.map((doc) => (
                        <li key={doc.id} className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-4 py-2.5">
                          <span className="flex items-center gap-2 text-sm truncate"><FileText className="h-4 w-4 text-orange-600 shrink-0" /><span className="truncate">{doc.filename}</span></span>
                          <button onClick={() => setDocuments((d) => d.filter((x) => x.id !== doc.id))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        </li>))}</ul>
                    )}
                  </div>
                )}

                {key === "review" && (
                  <div>
                    <h2 className="font-heading text-2xl tracking-tight text-primary">{t("wizard.reviewTitle")}</h2>
                    <div className="mt-6 grid sm:grid-cols-2 gap-5">
                      <div><Label htmlFor="con">{t("wizard.yourName")}</Label><Input id="con" className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="wizard-contact-name" /></div>
                      <div><Label htmlFor="ce">{t("wizard.email")}</Label><Input id="ce" type="email" className={inputCls} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="wizard-contact-email" /></div>
                      <div className="sm:col-span-2"><Label htmlFor="cp">{t("wizard.phoneOptional")}</Label><Input id="cp" className={inputCls} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></div>
                    </div>
                    <p className="mt-5 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> {t("wizard.securedStripe")}</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" onClick={back} disabled={step === 0} className="text-muted-foreground" data-testid="wizard-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> {t("common.back")}</Button>
            {step < lastStep ? (
              <Button onClick={next} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="wizard-next">{t("wizard.continue")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : needsQuote ? (
              <Button onClick={() => navigate("/contact")} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="wizard-request-quote">{t("wizard.requestQuotation")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="wizard-submit">
                {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> {t("wizard.redirecting")}</> : <>{t("wizard.pay", { amount: formatUSD(total) })} <Lock className="h-4 w-4 ml-1.5" /></>}
              </Button>
            )}
          </div>
        </div>

        {/* Order summary */}
        <aside className="lg:sticky lg:top-28 h-fit rounded-2xl border border-border bg-card p-6 card-soft" data-testid="wizard-summary">
          <h3 className="font-heading text-lg text-primary">{t("wizard.orderSummary")}</h3>
          <div className="mt-4 space-y-2.5 text-sm">
            {lineItems.length === 0 ? <p className="text-muted-foreground">{bankOnly ? t("wizard.loadingOrder") : t("wizard.selectCountryToBegin")}</p> : lineItems.map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3"><span className="text-muted-foreground">{it.label}</span><span className={`font-mono shrink-0 ${it.amount < 0 ? "text-green-700" : "text-foreground"}`}>{formatUSD(it.amount)}</span></div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <span className="text-muted-foreground text-sm">{t("wizard.totalDueToday")}</span>
            <span className="font-heading text-2xl text-primary">{formatUSD(total)}</span>
          </div>
          {!bankOnly && country && <p className="mt-3 text-xs text-muted-foreground">{t("wizard.renewalPerYear", { amount: formatUSD(country.annual_fee) })}</p>}
          <p className="mt-4 pt-3 border-t border-border text-[11px] leading-relaxed text-muted-foreground/80" data-testid="wizard-summary-legal-line">
            {t("wizard.billedBy")} {identity.legalName}{identity.regNumber !== REQUIRES_CONFIRMATION ? ` · ${t("wizard.regNo")} ${identity.regNumber}` : ""} · {identity.office}
          </p>
          <Disclaimer type="pricing" className="mt-3" />
          {includesBanking && <Disclaimer type="banking" className="mt-2" />}
        </aside>
      </div>
    </div>
  );
}

